import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { mergePolicyDetail, MergeRequestAction } from 'actions/policies';
import { fetchAccountInfo } from 'actions/user';
import { AxiosError } from 'axios';
import { hasAutomatedCancelProcess } from 'models/insurances/types/mapping';
import { OptionalPolicyAttributes, Policy } from 'models/policies';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AppState } from 'reducers';
import { ThunkDispatch } from 'redux-thunk';
import endpoint from 'shared/api';

export const getPolicy = async (
  policyId: string,
  dispatch: ThunkDispatch<AppState, unknown, MergeRequestAction>
): Promise<Policy> => {
  const { data } = await endpoint.getPolicyDetail(policyId);
  // TODO: remove once student card & health card check list items stop replying on policy selector
  dispatch(mergePolicyDetail(data));
  return data;
};

const getPolicyCancellation = async (
  policyId: string
): Promise<OptionalPolicyAttributes> => {
  const { data } = await endpoint.getPolicyCancellation(policyId);
  return data;
};

export const useGetPolicy = (
  policyId: string,
  options?: UseQueryOptions<Policy, AxiosError>
) => {
  const {
    isLoading: isGetPolicyLoading,
    data: policy,
    error: getPolicyError,
    refetch,
  } = useQuery<Policy, AxiosError>({
    queryKey: ['getPolicy', { policyId }],
    queryFn: () => getPolicy(policyId, dispatch),
    enabled: true,
    ...options,
  });
  const dispatch = useDispatch();

  useEffect(() => {
    // TODO: remove this step once we stop using account data from redux storage
    dispatch(fetchAccountInfo());
  }, [dispatch, policyId]);

  const getPolicyCancellationEnabled =
    !!policy &&
    hasAutomatedCancelProcess[policy.type] &&
    policy.status !== 'CANCELED';

  const {
    isLoading: isGetPolicyCancellationLoading,
    data: policyAttributes,
    error: getPolicyCancellationError,
  } = useQuery<Partial<OptionalPolicyAttributes>, AxiosError>({
    queryKey: ['getPolicyCancellation', { policyId }],
    queryFn: () => getPolicyCancellation(policyId),
    enabled: getPolicyCancellationEnabled,
  });

  return {
    error: getPolicyError || getPolicyCancellationError,
    isLoading:
      isGetPolicyLoading ||
      (getPolicyCancellationEnabled && isGetPolicyCancellationLoading),
    policy:
      policy !== undefined
        ? {
            ...policy,
            attributes: {
              ...policy.attributes,
              ...(policyAttributes ?? {}),
            },
          }
        : undefined,
    refetch,
  };
};
